import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { SttEngineService, SttState } from '../utils/speech-to-text/stt-engine.service';
import { SttContextService } from '../utils/speech-to-text/stt-context.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IAssessmentFrameworkDetail } from '../../ui-item-maker/item-set-editor/models/assessment-framework';
import { LangService } from 'src/app/core/lang.service';

@Component({
  selector: 'stt-toolbar',
  templateUrl: './stt-toolbar.component.html',
  styleUrls: ['./stt-toolbar.component.scss']
})
export class SttToolbarComponent implements OnInit, OnDestroy {
  @Input() asmtFmrk: IAssessmentFrameworkDetail;
  @Input() isPj: boolean = false;
  @Input() isG6: boolean = false;
  @Output() speechToTextChange = new EventEmitter<boolean>();

  @ViewChild('tooltipOverlay') overlayInstance: CdkConnectedOverlay;

  isRecordingSpeech: boolean = false;
  showTip = false;
  tipTitle = '';
  tipBody = '';
  private tipTimer: any;

  positions: ConnectedPosition[] = [{
    originX : 'center',  originY : 'bottom',
    overlayX: 'center',  overlayY: 'top',
    offsetY : 8
  }];

  private destroy$ = new Subject<void>();
  private sttBaseText = '';
  private sttInterimText = '';

  constructor(
    private sttEngine: SttEngineService,
    private sttCtx: SttContextService,
    private lang: LangService
  ) {}

  ngOnInit(): void {
    this.sttCtx.focusedInput$
      .pipe(takeUntil(this.destroy$))
      .subscribe(focused => {
        if (this.showTip && this.tipTitle.startsWith('Select') && focused) {
          this.showTip = false;
        }
      });

    this.sttEngine.getInterim()
      .pipe(takeUntil(this.destroy$))
      .subscribe(interimText => {
        this.sttInterimText = interimText;
        this.sttCtx.setFullText(this.sttBaseText + this.sttInterimText);
      });

    this.sttEngine.getTranscript()
      .pipe(takeUntil(this.destroy$))
      .subscribe(finalText => {
        this.sttBaseText += finalText.trim() + ' ';
        this.sttInterimText = '';
        this.sttCtx.setFullText(this.sttBaseText);
      });

    this.sttEngine.getState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => this.isRecordingSpeech = (state === SttState.LISTENING));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.tipTimer);
  }

  isSTTEnabled(): boolean {
    return this.asmtFmrk?.toolbarOptions?.stt ?? false;
  }

  onMicClick(): void {
    if (this.isRecordingSpeech && !this.sttCtx.getFocusedElement()) {
      this.stopRecording();
      this.showTip = false;
      return;
    }

    if (!this.sttCtx.getFocusedElement()) {
      const lang = this.lang.getCurrentLanguage();
      if (lang === 'fr') {
        this.showTooltip('Sélectionnez une zone de texte', 'Cliquez dans un champ de texte pour commencer à enregistrer…');
      } else {
        this.showTooltip('Select a text box', 'Click inside a text field to start recording…');
      }
      this.showTooltip('Select a text box','Click inside a text field to start recording…');
      this.startRecording();
      this.focusFirstInput();
      return;
    }

    if (!this.isRecordingSpeech) {
      const lang = this.lang.getCurrentLanguage();
      if (lang === 'fr') {
        this.showTooltip('Parlez', 'Parlez pour transformer vos mots en texte…');
      } else {
        this.showTooltip('Speak', 'Speak to turn your words into text…');
      }
      this.startRecording();
    } else {
      this.stopRecording();
      this.showTip = false;
    }
  }

  keepInputFocus(ev: MouseEvent) {
    ev.preventDefault();
  }

  private startRecording(): void {
    const focusedEl = this.sttCtx.getFocusedElement();
    if (focusedEl) {
      this.sttBaseText = focusedEl.value;
      if (this.sttBaseText.length > 0 && !this.sttBaseText.endsWith(' ')) {
        this.sttBaseText += ' ';
      }
    } else {
      this.sttBaseText = '';
    }
    this.sttInterimText = '';
    this.isRecordingSpeech = true;
    this.speechToTextChange.emit(true);
    this.sttEngine.start();
  }

  private stopRecording(): void {
    this.isRecordingSpeech = false;
    this.speechToTextChange.emit(false);
    this.sttEngine.stop();
  }

  private focusFirstInput() {
    const scope = document;
    const inputs = Array.from(
      scope.querySelectorAll<HTMLTextAreaElement>(
        'textarea[sttFocus]:not([disabled]):not([tabindex="-1"])'
      )
    );
    if (inputs.length) {
      setTimeout(() => inputs[0].focus({ preventScroll: true }), 0);
    }
  }

  private showTooltip(title: string, body: string, ms = 3000) {
    clearTimeout(this.tipTimer);
    this.tipTitle = title;
    this.tipBody  = body;
    this.showTip  = true;
    this.tipTimer = setTimeout(() => this.showTip = false, ms);
  }

  closeTip() { this.showTip = false; }
}


